import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useAuthState } from "@/hooks/useAuth"
import { addDoc, collection, serverTimestamp, query, where, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { uploadImageToStorage } from "@/lib/firebase"
import { trackCollectionCreated, trackFirstCollectionCreated } from "@/lib/metaPixel"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Upload } from "lucide-react"
import { useAnalytics } from "@/hooks/useAnalytics"
import { Keyboard } from '@capacitor/keyboard'
import { getPlatform } from '@/lib/platform'

interface CreateCollectionSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onCollectionCreated: () => void
}

export function CreateCollectionSheet({ 
  isOpen, 
  onOpenChange,
  onCollectionCreated
}: CreateCollectionSheetProps) {
  const { t } = useTranslation()
  const { user } = useAuthState()
  const { trackEvent } = useAnalytics()
  
  const [title, setTitle] = useState("")
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  // Keyboard tracking for iOS native - only run on iOS to avoid double keyboard handling
  useEffect(() => {
    const platform = getPlatform()
    if (platform !== 'ios') return

    let showListener: any
    let hideListener: any

    const setupKeyboardListeners = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setupKeyboardListeners()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])

  // Handle image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('common.error') + ': ' + t('common.fileTooLarge', 'File too large (max 5MB)'))
      return
    }

    setCoverImage(file)
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setCoverImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
    
    setError(null)
  }

  const handleCreateCollection = async () => {
    if (!user) return
    if (!title.trim()) {
      setError(t('common.error') + ': ' + t('profile.collectionTitleRequired', 'Collection title is required'))
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      let coverImageUrl: string | undefined
      
      // Upload cover image if provided
      if (coverImage) {
        const folder = `users/${user.uid}/collections`
        coverImageUrl = await uploadImageToStorage(coverImage, folder)
      }
      
      // Create collection document - only include coverImageUrl if it exists
      const collectionData: Record<string, any> = {
        userId: user.uid,
        title: title.trim(),
        createdAt: serverTimestamp(),
        isAnonymous: false,
        items: [],
        itemCount: 0
      }
      
      // Only add coverImageUrl if it was successfully uploaded
      if (coverImageUrl) {
        collectionData.coverImageUrl = coverImageUrl
      }
      
      const docRef = await addDoc(collection(db, 'collections'), collectionData)
      
      // Check if this is the user's first collection and track with Meta Pixel
      const collectionsRef = collection(db, 'collections')
      const userCollectionsQuery = query(
        collectionsRef,
        where('userId', '==', user.uid)
      )
      const existingCollections = await getDocs(userCollectionsQuery)
      const isFirstCollection = existingCollections.size === 1 // Just created, so size is 1
      
      trackEvent('collection_created', {
        collection_id: docRef.id,
        title: title.trim(),
        is_first: isFirstCollection
      })

      if (isFirstCollection) {
        trackFirstCollectionCreated(title.trim())
      } else {
        trackCollectionCreated(title.trim())
      }
      
      // Reset form and close sheet
      setTitle("")
      setCoverImage(null)
      setCoverImagePreview(null)
      onOpenChange(false)
      onCollectionCreated()
    } catch (err) {
      console.error('[CreateCollectionSheet] Error creating collection:', err)
      setError(t('common.error') + ': ' + t('profile.collectionCreateError', 'Failed to create collection'))
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-auto max-h-[80vh] flex flex-col p-6" 
        hideCloseButton
        style={{
          bottom: keyboardHeight > 0 
            ? `calc((100vh - ${keyboardHeight}px - 80vh) / 2 + ${keyboardHeight}px)` 
            : 0,
          transition: 'bottom 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        } as React.CSSProperties}
      >
        <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <SheetTitle className="text-xl font-semibold">
            {t('profile.createCollection')}
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Collection Title */}
          <div className="space-y-2">
            <Label htmlFor="collection-title">{t('profile.collectionTitle', 'Collection Title')}</Label>
            <Input
              id="collection-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('profile.collectionTitlePlaceholder', 'Enter collection name')}
              disabled={isCreating}
            />
          </div>
          
          {/* Cover Image Upload */}
          <div className="space-y-2">
            <Label>{t('profile.coverImage', 'Cover Image')} ({t('common.optional', 'Optional')})</Label>
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
              {coverImagePreview ? (
                <div className="relative w-full aspect-square max-w-[200px] mx-auto">
                  <img 
                    src={coverImagePreview} 
                    alt="Cover preview" 
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8"
                    onClick={() => {
                      setCoverImage(null)
                      setCoverImagePreview(null)
                    }}
                    disabled={isCreating}
                  >
                    &times;
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center cursor-pointer w-full py-6">
                  <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground text-center">
                    {t('profile.uploadCoverImage', 'Click to upload a cover image')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('common.maxFileSize', 'Max 5MB')}
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={isCreating}
                  />
                </label>
              )}
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="text-destructive text-sm">{error}</div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="flex gap-3 pt-8 pb-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className="flex-1"
            onClick={handleCreateCollection}
            disabled={isCreating || !title.trim()}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.creating', 'Creating...')}
              </>
            ) : (
              t('profile.createCollection')
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
